/**
 * SquarePaymentInfo.tsx
 * 予約コードに紐づくSquare請求書（items・決済状況）を表示する
 *
 * 会計の正は Square 側（予約データの料金フィールドとズレることがある）。
 * - パネルオープン時に refresh=1 で Square API へ実問い合わせ（常に最新）
 * - 請求書が複数ある場合（再発行・複数作成）は **ある分だけ** 1件ずつブロック表示
 * - 明細（line_items）は常時展開・請求書ごとの合計行付き
 * - Pusher payment.updated（webhook発火）で自動リフレッシュ（DB再読込）
 * - 発行 / Squareで開く / 取消 / invoice ID 入力は items の下に配置
 *   （数量変更などの編集は Square 側で行う運用）
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Chip, Typography, CircularProgress, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { SYS_API_BASE } from '../../../constants/calendar';
import { pusherService } from '../../../services/pusherService';
import { invoiceStatusChip, computeSquareAmounts } from '../../../utils/paymentStatus';
import { readCoveragePayment, coveragePaymentLabel } from '../../../lib/coveragePayment';

interface LineItem {
  name: string;
  quantity: string;
  amount?: number;
  base_price?: number;
  total?: number;
  // DB(sq_orders.line_items)にはSquare生データが入っており、金額はネスト形式
  total_money?: { amount?: number };
  base_price_money?: { amount?: number };
}

/** 変換済み（フラット）とSquare生データ（ネスト）の両形式から金額を解決 */
function lineItemAmount(item: LineItem): number | undefined {
  return (
    item.total ??
    item.total_money?.amount ??
    item.base_price ??
    item.base_price_money?.amount ??
    item.amount
  );
}

interface SquarePaymentData {
  status: string;
  invoices?: any[];
  orders?: any[];
  payments?: any[];
  refunds?: any[];
  total_count?: number;
  error?: string;
}

function formatYen(amount: number | null | undefined): string {
  if (amount == null) return '¥0';
  return `¥${amount.toLocaleString()}`;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return dateStr;
  }
}

/** 入金があったとみなす invoice ステータス（支払日を出す対象） */
const SETTLED_STATUSES = ['PAID', 'PARTIALLY_REFUNDED', 'REFUNDED'];

interface Props {
  contactCode: string | undefined | null;
  disabled?: boolean;
  /** 予約DB側の invoice_link（sq:0:inv:0-xxx 形式） */
  invoiceLink?: string;
  onInvoiceLinkChange?: (value: string) => void;
  onInvoiceOpen?: () => void;
  onInvoiceCreate?: () => void;
  onInvoiceVoid?: () => void;
  isCreatingInvoice?: boolean;
  isVoidingInvoice?: boolean;
  /** 予約で加入している補償（込みプランを含む）。加入している方だけ支払い判定を出す */
  nocJoined?: boolean;
  cdwJoined?: boolean;
}

export default function SquarePaymentInfo({
  contactCode,
  disabled,
  invoiceLink,
  onInvoiceLinkChange,
  onInvoiceOpen,
  onInvoiceCreate,
  onInvoiceVoid,
  isCreatingInvoice,
  isVoidingInvoice,
  nocJoined,
  cdwJoined,
}: Props) {
  const [data, setData] = useState<SquarePaymentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // invoiceLink は入力欄で編集できるため、依存に入れると打鍵ごとに
  // fetchData の同一性が変わり Square への実問い合わせが連発する。ref で読む。
  const invoiceLinkRef = useRef(invoiceLink);
  useEffect(() => { invoiceLinkRef.current = invoiceLink; }, [invoiceLink]);

  const fetchData = useCallback(
    async (silent: boolean = false, live: boolean = false) => {
      if (!contactCode) return;
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
        setData(null);
      }
      try {
        // live=true: Square API へ実問い合わせ（DB も更新される）
        // invoice_id: 予約が持つ invoice_link をヒントとして渡す（2026-08-08）。
        //   下書き(DRAFT)は webhook が飛ばず決済DBに行が無いことがあり、
        //   contact_code 検索だけでは見つからない。サーバ側がこのIDで
        //   Square から直接取得してDB登録（自己修復）する。
        const url =
          `${SYS_API_BASE}/payment/search_by_contact.php?contact_code=${encodeURIComponent(contactCode)}` +
          (live ? '&refresh=1' : '') +
          (invoiceLinkRef.current ? `&invoice_id=${encodeURIComponent(invoiceLinkRef.current)}` : '');
        const res = await fetch(url);
        const json = await res.json();
        setData(json);
      } catch (err) {
        setData({ status: 'error', error: String(err) });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [contactCode],
  );

  // パネルオープン時（contact_code 変化時）: Square 実問い合わせで常に最新を表示
  useEffect(() => {
    if (!contactCode) {
      setData(null);
      return;
    }
    fetchData(false, true);
  }, [contactCode, fetchData]);

  // webhook → Pusher payment.updated で自動リフレッシュ（DB再読込で軽量に）
  useEffect(() => {
    if (!contactCode) return;
    const unsubscribe = pusherService.subscribe((eventName: string, evData: any) => {
      if (eventName === 'payment.updated' && evData?.contact_code === contactCode) {
        fetchData(true, false);
      }
    });
    return unsubscribe;
  }, [contactCode, fetchData]);

  if (!contactCode) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">Square確認中...</Typography>
      </Box>
    );
  }

  if (!data) return null;

  const invoices = data.invoices || [];
  const orders = data.orders || [];
  const payments = data.payments || [];
  const refunds = data.refunds || [];
  const noInvoice =
    data.status === 'error' || (data.status === 'ok' && invoices.length === 0);

  // 補償の支払い判定（サーバー側 coverage_payment.php。invoice_id を渡したときだけ返る）
  const coveragePayment = readCoveragePayment(data);
  const coverageRows = coveragePayment
    ? ([['NOC', nocJoined, coveragePayment.noc], ['CDW', cdwJoined, coveragePayment.cdw]] as const)
        .filter(([, joined]) => joined)
    : [];

  // 決済額 / 返金額 / 実質支払額（返金がある時だけサマリ行を出す）
  const amounts = computeSquareAmounts({ invoices, payments, orders, refunds, fetchedAt: 0 });

  // 請求書ごとの明細を order から解決
  const itemsForInvoice = (inv: any): LineItem[] => {
    const order = orders.find((o: any) => o.order_id === inv.order_id);
    return (order?.line_items as LineItem[]) || [];
  };

  const buttonStyle: React.CSSProperties = {
    padding: '4px 12px',
    fontSize: '0.75rem',
    borderRadius: 4,
    cursor: disabled ? 'default' : 'pointer',
  };

  return (
    <Box
      sx={{
        mx: 1,
        my: 0.5,
        px: 1.25,
        py: 1,
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 1.5,
      }}
    >
      {/* ヘッダー: タイトル + 更新ボタン + 返金チップ */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', mr: 0.5 }}
        >
          Square請求書
        </Typography>
        <IconButton
          size="small"
          onClick={() => fetchData(true, true)}
          disabled={refreshing}
          sx={{ p: 0.25 }}
          title="Squareに問い合わせて最新の請求書を取得"
        >
          {refreshing ? (
            <CircularProgress size={14} />
          ) : (
            <RefreshIcon sx={{ fontSize: 16, color: '#64748b' }} />
          )}
        </IconButton>
        {invoices.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            ({invoices.length}件)
          </Typography>
        )}
        {noInvoice && (
          <Typography variant="caption" color="text.disabled">
            Square未登録
          </Typography>
        )}
        {refunds.map((ref: any, idx: number) => (
          <Chip
            key={`refund-${idx}`}
            label={`返金 ${formatYen(Number(ref.amount) || 0)}${ref.created_at ? ` (${formatDate(ref.created_at)})` : ''}`}
            size="small"
            sx={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
            }}
          />
        ))}
      </Box>

      {/* 決済 / 返金 / 実質 サマリ（返金がある予約のみ）。
          一部返金だと請求合計＝決済額とは一致しなくなるため、内訳を明示する。 */}
      {amounts.hasRefunds && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: 1.25,
            mt: 0.75,
            px: 1,
            py: 0.5,
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', color: '#7c2d12' }}>
            決済 <b>{formatYen(amounts.paidAmount)}</b>
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#c62828' }}>
            返金 <b>-{formatYen(amounts.refundAmount)}</b>
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 700 }}>
            実質 {formatYen(amounts.netAmount)}
          </Typography>
        </Box>
      )}

      {/* 請求書ごとのブロック（複数ある分だけ表示） */}
      {invoices.map((inv: any, invIdx: number) => {
        const chip = invoiceStatusChip(inv.status);
        const payment = payments.find((p: any) => p.order_id === inv.order_id);
        const payDate = SETTLED_STATUSES.includes(inv.status)
          ? (payment?.created_at || inv.updated_at)
          : null;
        const items = itemsForInvoice(inv);

        return (
          <Box
            key={inv.invoice_id}
            sx={{
              mt: 0.75,
              pt: invIdx > 0 ? 0.75 : 0,
              borderTop: invIdx > 0 ? '1px dashed #cbd5e1' : 'none',
            }}
          >
            {/* ステータス行 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={`${chip.label} ${formatYen(inv.computed_amount)}${payDate ? ` (${formatDate(payDate)})` : ''}`}
                size="small"
                sx={{
                  backgroundColor: chip.bg,
                  color: chip.color,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
              {inv.invoice_number && (
                <Typography variant="caption" color="text.secondary">
                  #{inv.invoice_number}
                </Typography>
              )}
            </Box>

            {/* 明細（items）: 常時展開 */}
            {items.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                {items.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 0.75,
                      py: 0.25,
                      fontSize: '0.78rem',
                      lineHeight: 1.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 'inherit', color: '#334155', flex: 1, minWidth: 0 }}
                    >
                      {item.name}
                    </Typography>
                    <Typography sx={{ fontSize: 'inherit', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      ×{item.quantity}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 'inherit',
                        color: '#0f172a',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        minWidth: 64,
                        textAlign: 'right',
                      }}
                    >
                      {formatYen(lineItemAmount(item))}
                    </Typography>
                  </Box>
                ))}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.75,
                    mt: 0.5,
                    pt: 0.5,
                    borderTop: '1px solid #cbd5e1',
                  }}
                >
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', flex: 1 }}>
                    請求合計
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}
                  >
                    {formatYen(inv.computed_amount)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        );
      })}

      {/* 補償の支払い: 予約で加入している補償が、Square の請求書で支払われたか */}
      {coverageRows.length > 0 && coveragePayment && (
        <Box
          data-testid="coverage-payment"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.75 }}
        >
          <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mr: 0.25 }}>補償の支払い</Typography>
          {coverageRows.map(([label, , status]) => {
            const l = coveragePaymentLabel(status, coveragePayment.reason);
            return (
              <Chip
                key={label}
                label={`${label} ${l.text}`}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, color: l.color, backgroundColor: l.bg }}
              />
            );
          })}
        </Box>
      )}

      {/* 操作: 発行 / Squareで開く / 取消 + invoice ID（詳細設定から移設） */}
      {(onInvoiceOpen || onInvoiceCreate || onInvoiceVoid) && (
        <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
            {invoiceLink && onInvoiceOpen && (
              <button
                type="button"
                onClick={onInvoiceOpen}
                style={{
                  ...buttonStyle,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#334155',
                  cursor: 'pointer',
                }}
              >
                Squareで開く
              </button>
            )}
            {invoiceLink ? (
              onInvoiceVoid && (
                <button
                  type="button"
                  onClick={onInvoiceVoid}
                  disabled={disabled || isVoidingInvoice}
                  style={{
                    ...buttonStyle,
                    border: '1px solid #ef4444',
                    background: '#fff',
                    color: '#ef4444',
                  }}
                >
                  {isVoidingInvoice ? '取消中…' : '取消'}
                </button>
              )
            ) : (
              onInvoiceCreate && (
                <button
                  type="button"
                  onClick={onInvoiceCreate}
                  disabled={disabled || isCreatingInvoice}
                  style={{
                    ...buttonStyle,
                    border: 'none',
                    background: '#3b82f6',
                    color: '#fff',
                  }}
                >
                  {isCreatingInvoice ? '発行中…' : '発行'}
                </button>
              )
            )}
          </Box>
          <input
            type="text"
            value={invoiceLink || ''}
            onChange={(e) => onInvoiceLinkChange?.(e.target.value)}
            disabled={disabled}
            placeholder="invoice ID（例: sq:0:inv:0-xxx）"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '4px 8px',
              fontSize: '0.75rem',
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              color: '#64748b',
              background: disabled ? '#f8fafc' : '#fff',
            }}
          />
        </Box>
      )}
    </Box>
  );
}
